"use client";

import type { Events as EventsType, Event as EventType } from "@types";
import { useMemo, useState } from "react";
import { DotsIcon } from "@/components/icons/dots";
import AnimatedTitle from "@/components/inputs/AnimatedTitle";
import { forms, spacing, typography } from "@/styles/design-tokens";
import { BlockContent } from "./inputs/PortableTextComponents";

type ExpandedEvents = Omit<EventsType, "eventsList"> & {
  eventsList?: EventType[]; // Override to fully expanded EventType array
};

// Converts hour (0–23) into "h AM/PM" for hour dividers
function formatHourWithAmPm(hour24: number): string {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return `${hour12} ${period}`;
}

// Parses "hh:mm AM/PM" → { hours: number (0–23), minutes: number }
function parseTime(timeStr: string): { hours: number; minutes: number } {
  if (!timeStr) return { hours: 0, minutes: 0 };

  const [time, period] = timeStr.split(" ");
  const [hStr, mStr] = time.split(":");
  let hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);

  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  return { hours, minutes };
}

// Converts "hh:mm AM/PM" to total minutes (for sorting)
function timeToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const { hours, minutes } = parseTime(timeStr);
  return hours * 60 + minutes;
}

// Date filter value for events that don't have a date yet
const DATE_TBD = "DATE TBD";

// Date filter value for showing every day at once
const ALL_DAYS = "ALL DAYS";

// A day is also divided by hour when at least MIN_CROWDED_HOURS of its hours
// have more than MAX_EVENTS_PER_HOUR events starting in them. Otherwise the
// hour dividers would mostly hold a single event each.
const MAX_EVENTS_PER_HOUR = 2;
const MIN_CROWDED_HOURS = 2;

type EventGroup = { key: string; label: string; events: EventType[] };

// Formats "YYYY-MM-DD" → "SAT, OCT 4". Parsed as UTC so server and client
// render the same label regardless of the visitor's timezone.
function formatDate(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00Z`)
    .toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })
    .toUpperCase();
}

// Formats "YYYY-MM-DD" → "Monday" for event cards
function formatWeekday(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });
}

// Formats "YYYY-MM-DD" → "Monday, August 5" for day dividers
function formatDay(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

// For the day dividers in the schedule, and the gray hour dividers within
// a busy day
function GroupDivider({ label, hour }: { label: string; hour?: boolean }) {
  return (
    <div
      className={`w-full col-span-full flex gap-4 items-center justify-center mb-6 ${hour ? "text-neutral-600" : ""}`}
    >
      <p
        className={`${hour ? typography.caption : typography.captionLarge} w-fit whitespace-nowrap`}
      >
        {label}
      </p>
      <div className="h-2 w-full border-t-2" />
    </div>
  );
}

function EventGrid({ events }: { events: EventType[] }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
      {events.map((event) => (
        <Event key={event._id} {...event} />
      ))}
    </div>
  );
}

// Formats "hh:mm AM/PM" → "h:mmam/pm" for UI labels
function formatTimeToAmPm(timeStr: string): string {
  if (!timeStr) return "";
  const { hours, minutes } = parseTime(timeStr);
  const ampm = hours >= 12 ? "pm" : "am";
  const hour12 = hours % 12 || 12;
  const minuteStr = minutes.toString().padStart(2, "0");
  return `${hour12}${minutes !== 0 ? `:${minuteStr}` : ""}${ampm}`;
}

function Event({
  type,
  name,
  stage,
  description,
  date,
  duration,
  address,
}: EventType) {
  const start = duration?.start; // now like "08:30 AM"
  const end = duration?.end;

  let timeLabel: string | undefined;
  if (start && end) {
    timeLabel = `${formatTimeToAmPm(start)} – ${formatTimeToAmPm(end)}`;
  } else if (start) {
    timeLabel = `${formatTimeToAmPm(start)} – until`;
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-black shadow-[8px_8px_0_0_rgba(0,0,0,0.25)] flex flex-col p-6 gap-4 text-left">
      <p className={typography.caption}>
        {[date ? formatWeekday(date) : DATE_TBD, timeLabel]
          .filter(Boolean)
          .join(" · ")}
      </p>

      <div className="space-y-1 flex-1">
        {name && (
          <p className="text-pretty font-black font-display text-2xl tracking-wide">
            {name}
          </p>
        )}
        {/* Descriptions written before this field became rich text are
            still plain strings in the dataset */}
        {typeof description === "string" ? (
          <p className={typography.body}>{description}</p>
        ) : (
          description && <BlockContent value={description} />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {stage &&
          (address ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${stage}, ${address}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Get directions to ${address}`}
              className={`${typography.captionSmall} bg-orange border-2 rounded-sm py-1.5 px-3 underline transition-colors hover:bg-white`}
            >
              {stage}
            </a>
          ) : (
            <p
              className={`${typography.captionSmall} bg-orange border-2 rounded-sm py-1.5 px-3`}
            >
              {stage}
            </p>
          ))}
        {type && (
          <p
            className={`${typography.captionSmall} bg-blue border-2 rounded-sm py-1.5 px-3`}
          >
            {type}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Events({ title, copy, eventsList }: ExpandedEvents) {
  const safeEventsList = eventsList ?? [];

  // Alphabetically sorted unique types and stages for filters
  const uniqueTypes = useMemo(() => {
    const types = Array.from(
      new Set(safeEventsList.map((e) => e.type).filter(Boolean)),
    );
    types.sort((a, b) => a.localeCompare(b));
    return ["ALL EVENTS", ...types];
  }, [safeEventsList]);

  const uniqueStages = useMemo(() => {
    const stages = Array.from(
      new Set(safeEventsList.map((e) => e.stage).filter(Boolean)),
    );
    stages.sort((a, b) => a.localeCompare(b));
    return ["ALL STAGES", ...stages];
  }, [safeEventsList]);

  // Chronologically sorted unique dates ("YYYY-MM-DD" sorts as a string),
  // with "Date TBD" last if any event has no date
  const uniqueDates = useMemo(() => {
    const dates = Array.from(
      new Set(
        safeEventsList
          .map((e) => e.date)
          .filter((date): date is string => !!date),
      ),
    );
    dates.sort();
    if (safeEventsList.some((e) => !e.date)) dates.push(DATE_TBD);
    return dates;
  }, [safeEventsList]);

  // Filter states
  const [selectedType, setSelectedType] = useState("ALL EVENTS");
  const [selectedStage, setSelectedStage] = useState("ALL STAGES");
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  // Every day is shown at once by default
  const dateOptions = useMemo(() => [ALL_DAYS, ...uniqueDates], [uniqueDates]);

  // Falls back to all days if the selected date disappears after a content
  // update
  const activeDate =
    selectedDate && dateOptions.includes(selectedDate)
      ? selectedDate
      : ALL_DAYS;

  // Filter and sort events: by start time asc, then duration desc
  const filteredEvents = useMemo(() => {
    return safeEventsList
      .filter(
        (e) =>
          activeDate === ALL_DAYS ||
          (activeDate === DATE_TBD ? !e.date : e.date === activeDate),
      )
      .filter((e) => selectedType === "ALL EVENTS" || e.type === selectedType)
      .filter(
        (e) => selectedStage === "ALL STAGES" || e.stage === selectedStage,
      )
      .sort((a, b) => {
        const startA = timeToMinutes(a.duration?.start ?? "");
        const startB = timeToMinutes(b.duration?.start ?? "");
        if (startA !== startB) return startA - startB;

        const endA = timeToMinutes(a.duration?.end ?? "") || startA + 1;
        const endB = timeToMinutes(b.duration?.end ?? "") || startB + 1;
        const durationA = endA - startA;
        const durationB = endB - startB;

        // Longer duration first if same start time
        return durationB - durationA;
      });
  }, [safeEventsList, activeDate, selectedType, selectedStage]);

  // Group events by day, each day sorted by start time with untimed events
  // last. Days are in chronological order, with "Date TBD" last. Days with
  // several crowded hours are further divided by hour, followed by events
  // with an undefined start time.
  const dayGroups = useMemo(() => {
    const eventsByDay: { [date: string]: EventType[] } = {};
    filteredEvents.forEach((event) => {
      const date = event.date || DATE_TBD;
      if (!eventsByDay[date]) eventsByDay[date] = [];
      eventsByDay[date].push(event);
    });

    return uniqueDates
      .filter((date) => eventsByDay[date])
      .map((date) => {
        const withStartTime = eventsByDay[date].filter(
          (e) => !!e.duration?.start,
        );
        const withoutStartTime = eventsByDay[date].filter(
          (e) => !e.duration?.start,
        );

        let hourGroups: EventGroup[] = [];
        withStartTime.forEach((event) => {
          const { hours } = parseTime(event.duration?.start ?? "");
          const lastGroup = hourGroups.at(-1);
          if (lastGroup?.key === String(hours)) {
            lastGroup.events.push(event);
          } else {
            hourGroups.push({
              key: String(hours),
              label: formatHourWithAmPm(hours),
              events: [event],
            });
          }
        });

        const crowdedHours = hourGroups.filter(
          (group) => group.events.length > MAX_EVENTS_PER_HOUR,
        ).length;
        if (crowdedHours < MIN_CROWDED_HOURS) {
          hourGroups = [];
        } else if (withoutStartTime.length > 0) {
          hourGroups.push({
            key: "time-tbd",
            label: "Time TBD",
            events: withoutStartTime,
          });
        }

        return {
          key: date,
          label: date === DATE_TBD ? "Date TBD" : formatDay(date),
          events: [...withStartTime, ...withoutStartTime],
          hourGroups,
        };
      });
  }, [filteredEvents, uniqueDates]);

  if (!eventsList) return null;

  return (
    <section className={`${spacing.outer} bg-yellow`} id="events">
      <div className={spacing.inner}>
        <div className="text-center space-y-4 max-w-5xl mx-auto">
          {title && (
            <AnimatedTitle
              className={`${typography.h4} text-yellow text-stroke`}
              text={title}
            />
          )}
          {copy && <BlockContent value={copy} />}
        </div>

        {/* Filters */}
        <div className="w-full max-w-5xl mx-auto gap-4 flex flex-col items-center justify-center sm:flex-row">
          {uniqueDates.length > 0 && (
            <select
              className={forms.select}
              value={activeDate}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedDate(e.target.value)
              }
              aria-label="Filter by date"
            >
              {dateOptions.map((date) => (
                <option key={date} value={date}>
                  {date === DATE_TBD || date === ALL_DAYS
                    ? date
                    : formatDate(date)}
                </option>
              ))}
            </select>
          )}

          <select
            className={forms.select}
            value={selectedStage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setSelectedStage(e.target.value)
            }
            aria-label="Filter by stage"
          >
            {uniqueStages.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </select>

          <select
            className={forms.select}
            value={selectedType}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setSelectedType(e.target.value)
            }
            aria-label="Filter by event type"
          >
            {uniqueTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Render events grouped by day, and by hour within busy days */}
        <div className="w-full space-y-10 lg:space-y-20">
          {dayGroups.length === 0 ? (
            <p>No events match the selected filters.</p>
          ) : (
            dayGroups.map((day) => (
              <div key={day.key}>
                <GroupDivider label={day.label} />
                {day.hourGroups.length > 0 ? (
                  <div className="space-y-10">
                    {day.hourGroups.map((group) => (
                      <div key={group.key}>
                        <GroupDivider label={group.label} hour />
                        <EventGrid events={group.events} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <EventGrid events={day.events} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <DotsIcon classes="absolute -top-6 -left-6" color="#dd3163" />
      <DotsIcon
        classes="absolute rotate-180 -bottom-6 -right-6"
        color="#dd3163"
      />
    </section>
  );
}
